/**
 * Print typography shared by the two newspaper fronts. The existing editorial
 * grids and format-specific fitting remain responsible for all page geometry.
 *
 * The reference papers separate the nameplate, reading type, folio and rules:
 * a literary Mincho, quiet regular copy, precise figures and two weights of ink.
 */
export const newspaperEditorialStyles = `
.newspaper,.horizontal-newspaper{
  --news-serif:'Noto Serif JP Variable',serif;
  --news-sans:'Noto Sans JP Variable',sans-serif;
  --news-figure:'Bodoni Moda','Noto Serif JP Variable',serif;
  --news-hairline:calc(var(--page-w) * .00075);
  font-family:var(--news-serif);
  font-weight:400;
  font-synthesis:none;
}

/* A warm stock and three strengths of rule give the dense broadsheet air. */
.newspaper{
  --ink:#211e19;
  --paper:#f5f1e7;
  --rule:#777065;
  --hair:rgba(65,55,41,.26);
  --accent:#9e3026;
  --leaf:#737c64;
}
.newspaper .np-top-rail{
  line-height:1.55;
  border-bottom:calc(var(--page-w) * .0023) double var(--ink);
  font-family:var(--news-sans);
  font-weight:400;
  color:#655f54;
}
.newspaper .np-top-rail b{
  color:var(--ink);
  font-family:var(--news-serif);
  font-weight:700;
  letter-spacing:.12em;
}
.newspaper .np-headline-banner{color:var(--paper)}
.newspaper .np-title-deck h2,
.newspaper .newspaper-masthead h1,
.newspaper .np-story-copy h3{
  font-family:var(--news-serif);
  font-weight:700;
}
.newspaper .np-title-deck p{
  font-family:var(--news-serif);
  font-weight:400;
  letter-spacing:.1em;
}
.newspaper .newspaper-masthead{
  border-width:calc(var(--page-w) * .0018);
  border-color:var(--ink);
}
.newspaper .newspaper-masthead:after{
  inset:calc(var(--page-w) * .0045);
  border-width:var(--news-hairline);
}
.newspaper [data-newspaper-frame='double-inset'],
.newspaper [data-newspaper-frame='single-rule'],
.newspaper [data-newspaper-frame='corner-rule']{
  border-width:var(--news-hairline);
}
.newspaper [data-newspaper-frame='double-inset']:after{
  inset:calc(var(--page-w) * .0036);
  border-width:var(--news-hairline);
}
.newspaper [data-newspaper-frame='single-rule']:after{border:0}
.newspaper [data-newspaper-card] h3,
.newspaper .np-rail-note h2{
  border-bottom-width:var(--news-hairline);
  font-family:var(--news-sans);
  font-weight:700;
  letter-spacing:.045em;
}
.newspaper .np-news-card h4,
.newspaper .np-milestones-card h4,
.newspaper .np-small-news-card h4{
  font-family:var(--news-serif);
  font-weight:700;
}
.newspaper .np-story-copy p,
.newspaper .np-message-card p,
.newspaper .np-news-card p,
.newspaper .np-milestones-card p,
.newspaper .np-small-news-card p,
.newspaper .np-rail-note>p{font-weight:400}
.newspaper .np-publisher,
.newspaper .np-masthead-meta,
.newspaper .np-message-card small,
.newspaper .np-best-shot-card figcaption,
.newspaper .np-rail-note>small,
.newspaper>.brand{
  font-family:var(--news-sans);
  font-weight:400;
  color:#696154;
}
.newspaper .np-masthead-meta time{
  font-family:var(--news-figure);
  font-weight:500;
  color:var(--ink);
  font-variant-numeric:lining-nums tabular-nums;
}
.newspaper .np-masthead-meta b{font-weight:400}
.newspaper .np-publisher,
.newspaper .np-masthead-meta{border-color:var(--hair)}
.newspaper .np-extra-edition strong{
  font-family:var(--news-serif);
  font-weight:700;
  color:var(--paper);
}
.newspaper .np-extra-edition p{
  font-family:var(--news-sans);
  font-weight:400;
}
.newspaper .np-message-card:before,
.newspaper .np-rail-note:before{opacity:.5}

/* The horizontal edition shares the type roles while retaining its own grid. */
.horizontal-newspaper{
  --hn-ink:#211e19;
  --hn-rule:#4c453a;
  --hn-hair:#b8afa0;
  --hn-soft:#d6cec1;
  --hn-paper:#faf7f0;
  --hn-green:#737c64;
  --hn-hairline:var(--news-hairline);
  border-color:var(--hn-soft);
}
.horizontal-newspaper .hn-folio{
  line-height:1.55;
  border-bottom-width:calc(var(--page-w) * .003);
  font-family:var(--news-sans);
  font-weight:400;
  color:#706859;
}
.horizontal-newspaper .hn-folio b{
  font-family:var(--news-serif);
  font-weight:700;
  color:var(--hn-ink);
}
.horizontal-newspaper .hn-top-teasers{
  border-bottom-width:calc(var(--page-w) * .0014);
}
.horizontal-newspaper .hn-teaser{border-right-width:var(--news-hairline)}
.horizontal-newspaper .hn-masthead-band>h1,
.horizontal-newspaper .hn-lead-story>h1{
  font-family:var(--news-serif);
  font-weight:700;
}
.horizontal-newspaper .hn-masthead-band>h1{letter-spacing:.085em;font-size:calc(var(--page-w) * .0815 * var(--hn-scale) * var(--hn-name-fit,1));line-height:1.5}
.horizontal-newspaper .hn-issue-meta span{line-height:1.5}
.horizontal-newspaper .hn-masthead-note i{opacity:.82}
.horizontal-newspaper .hn-masthead-note p{font-weight:400}
.horizontal-newspaper .hn-teaser h2,
.horizontal-newspaper .hn-side-item h3,
.horizontal-newspaper .hn-mid-grid h2,
.horizontal-newspaper .hn-lower-grid h2{
  font-family:var(--news-sans);
  font-weight:700;
  letter-spacing:.015em;
}
.horizontal-newspaper .hn-teaser h2,.horizontal-newspaper .hn-side-item h3{line-height:1.5}
.horizontal-newspaper .hn-side-item h3 span{
  font-weight:400;
  letter-spacing:.075em;
}
.horizontal-newspaper .hn-lead-columns p,
.horizontal-newspaper .hn-teaser p,
.horizontal-newspaper .hn-side-item p,
.horizontal-newspaper .hn-family-message p,
.horizontal-newspaper .hn-lower-grid p,
.horizontal-newspaper .hn-lower-grid blockquote{
  font-family:var(--news-serif);
  font-weight:400;
}
.horizontal-newspaper .hn-lead-caption,
.horizontal-newspaper .hn-teaser small,
.horizontal-newspaper .hn-side-item>small,
.horizontal-newspaper .hn-family-message small,
.horizontal-newspaper .hn-best-shot figcaption,
.horizontal-newspaper .hn-issue-meta{
  font-family:var(--news-sans);
  font-weight:400;
  color:#756c5e;
}
.horizontal-newspaper .hn-issue-meta b{
  font-family:var(--news-figure);
  font-weight:500;
  color:var(--hn-ink);
  font-variant-numeric:lining-nums tabular-nums;
}
.horizontal-newspaper .hn-issue-meta time{font-weight:400}
.horizontal-newspaper .hn-record dt{
  font-family:var(--news-sans);
  font-weight:400;
  color:#756c5e;
}
.horizontal-newspaper .hn-record dd{
  font-family:var(--news-serif);
  font-weight:700;
  font-variant-numeric:lining-nums tabular-nums;
}
.horizontal-newspaper .hn-quote,
.horizontal-newspaper .hn-editorial-note{border-radius:0}
.horizontal-newspaper .hn-sprig-cut,
.horizontal-newspaper .hn-editorial-cut{opacity:.45}

/* Long submissions retain the same grid with a compact reading size in the
   small boxes. The normal edition keeps its more generous typesetting. */
body[data-copy-density='dense'] .newspaper .np-message-card p{
  max-width:100%;
  font-size:calc(var(--page-w) * .012 * var(--np-scale));
  line-height:1.6;
  margin-top:.5em;
}
body[data-copy-density='dense'] .newspaper .np-news-card p{
  font-size:calc(var(--page-w) * .0118 * var(--np-scale));
  line-height:1.55;
}
body[data-copy-density='dense'] .newspaper .np-news-card h4{
  font-size:calc(var(--page-w) * .012 * var(--np-scale));
  line-height:1.3;
}
body[data-copy-density='dense'] .newspaper .np-lower-card-grid h3{
  font-size:calc(var(--page-w) * .0135 * var(--np-scale));
  letter-spacing:.015em;
}
body[data-copy-density='dense'] .newspaper .np-milestones-card h4,
body[data-copy-density='dense'] .newspaper .np-small-news-card h4{
  font-size:calc(var(--page-w) * .0115 * var(--np-scale));
  line-height:1.35;
  margin-top:.4em;
}
body[data-copy-density='dense'] .newspaper .np-milestones-card p,
body[data-copy-density='dense'] .newspaper .np-small-news-card p{
  font-size:calc(var(--page-w) * .011 * var(--np-scale));
  line-height:1.48;
}
body[data-copy-density='dense'] .newspaper .np-topics-card ul{
  font-size:calc(var(--page-w) * .0108 * var(--np-scale));
  line-height:1.45;
}
body[data-copy-density='dense'] .newspaper .np-topics-card li{padding-block:.18em}
body[data-copy-density='dense'] .newspaper .np-message-card:before{display:none}

body[data-copy-density='dense'] .horizontal-newspaper .hn-mid-grid ul{
  font-size:calc(var(--page-w) * .0105 * var(--hn-scale));
  line-height:1.5;
  letter-spacing:0;
}
body[data-copy-density='dense'] .horizontal-newspaper .hn-mid-grid li{
  padding-left:.85em;
  padding-block:.08em;
}
body[data-copy-density='dense'] .horizontal-newspaper .hn-watch-note p,
body[data-copy-density='dense'] .horizontal-newspaper .hn-quote blockquote,
body[data-copy-density='dense'] .horizontal-newspaper .hn-editorial-note p{
  padding-right:0;
  font-size:calc(var(--page-w) * .0102 * var(--hn-scale));
  line-height:1.5;
}
body[data-copy-density='dense'] .horizontal-newspaper .hn-quote h2,
body[data-copy-density='dense'] .horizontal-newspaper .hn-editorial-note h2{
  font-size:calc(var(--page-w) * .013 * var(--hn-scale));
  margin-bottom:.25em;
}
body[data-copy-density='dense'] .horizontal-newspaper .hn-quote .hn-editorial-cut,
body[data-copy-density='dense'] .horizontal-newspaper .hn-editorial-note .hn-editorial-cut{display:none}
`;
